import { useI18n } from '@ecom/i18n';
import { EmptyState } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useState } from 'react';

import { useSalesBySourceQuery } from '../api';
import { type SalesBySourceEntry } from '../types';

import { CountUp } from '@/components/ui/CountUp';
import { SOURCE_COLOR, sourceLabel } from '@/features/orders/attribution';
import { formatCatalogPrice } from '@/features/products/format-price';

/**
 * « Ventes par réseau » (migration 0097) — revenue and orders per channel: social apps (from links, ads or the app's
 * in-app browser), search, manual channels. Same counting rule as the revenue figure above (every non-cancelled
 * order once), so the segments add up to it.
 *
 * Shown as a ring (each channel's share of the revenue, drawn when the screen opens) beside a compact legend; the
 * explanation of how a channel is decided sits behind an ⓘ instead of a paragraph.
 */

const RADIUS = 70;
const STROKE = 22;
/** A thin gap between two segments, in % of the ring, so neighbouring greys stay apart. */
const GAP = 0.6;

function colourOf(source: string): string {
  return SOURCE_COLOR[source] ?? 'var(--ecom-accent)';
}

function Ring({
  entries,
  totalRevenue,
}: {
  entries: readonly SalesBySourceEntry[];
  totalRevenue: number;
}): ReactElement {
  const { t } = useI18n();
  // Segments grow from 0 to their share right after the first paint (a CSS transition, neutralised by the
  // reduced-motion rule), each a little after the previous one.
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setDrawn(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, []);

  let offset = 0;
  const segments = entries.map((entry, index) => {
    const share = totalRevenue > 0 ? (entry.revenue / totalRevenue) * 100 : 0;
    const length = Math.max(share - (entries.length > 1 ? GAP : 0), 0.4);
    const segment = { source: entry.source, share, length, offset, index };
    offset += share;
    return segment;
  });
  const summary = entries
    .map((entry) => {
      const share = totalRevenue > 0 ? Math.round((entry.revenue / totalRevenue) * 100) : 0;
      return `${sourceLabel(entry.source, t)} ${String(share)} %`;
    })
    .join(', ');

  return (
    <div className="relative mx-auto h-48 w-48 shrink-0">
      <svg
        viewBox="0 0 200 200"
        className="h-full w-full -rotate-90"
        role="img"
        aria-label={summary}
      >
        <circle
          cx="100"
          cy="100"
          r={RADIUS}
          fill="none"
          stroke="var(--ecom-surface-2)"
          strokeWidth={STROKE}
        />
        {segments.map((segment) => (
          <circle
            key={segment.source}
            cx="100"
            cy="100"
            r={RADIUS}
            fill="none"
            stroke={colourOf(segment.source)}
            strokeWidth={STROKE}
            pathLength={100}
            strokeDasharray={`${String(drawn ? segment.length : 0)} 100`}
            strokeDashoffset={-segment.offset}
            style={{
              transition: 'stroke-dasharray 0.9s cubic-bezier(0.22, 1, 0.36, 1)',
              transitionDelay: `${String(segment.index * 90)}ms`,
            }}
          />
        ))}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-xs font-medium text-inkFaint">{t('merchant.attribution.total')}</span>
        <span className="num text-sm font-bold text-ink">
          <CountUp value={formatCatalogPrice(totalRevenue)} />
        </span>
      </div>
    </div>
  );
}

export function SalesBySourceCard(): ReactElement | null {
  const { t } = useI18n();
  const query = useSalesBySourceQuery();
  if (query.isPending || query.isError) return null;
  const { entries, totalRevenue } = query.data;

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="font-display text-lg font-semibold text-ink">
          {t('merchant.attribution.title')}
        </h2>
        <span
          className="inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full border border-line text-[11px] font-bold text-inkFaint"
          title={`${t('merchant.attribution.subtitle')} ${t('merchant.attribution.howItWorks')}`}
          aria-label={t('merchant.attribution.howItWorks')}
          role="note"
        >
          i
        </span>
      </div>
      {entries.length === 0 ? (
        <EmptyState title={t('merchant.attribution.empty')} />
      ) : (
        <div className="flex flex-col gap-6 rounded-xl2 border border-line bg-surface p-5 shadow-card md:flex-row md:items-center">
          <Ring entries={entries} totalRevenue={totalRevenue} />
          <ul className="anim-stagger flex min-w-0 flex-1 flex-col divide-y divide-line">
            {entries.map((entry) => {
              const share = totalRevenue > 0 ? entry.revenue / totalRevenue : 0;
              return (
                <li
                  key={entry.source}
                  className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <span
                    aria-hidden="true"
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ background: colourOf(entry.source) }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">
                      {sourceLabel(entry.source, t)}
                    </p>
                    <p className="text-xs text-inkFaint">
                      {t('merchant.attribution.orders', {
                        count: entry.orderCount,
                        plural: entry.orderCount > 1 ? 's' : '',
                      })}{' '}
                      ·{' '}
                      {t('merchant.attribution.average', {
                        amount: formatCatalogPrice(entry.averageOrderValue),
                      })}
                    </p>
                  </div>
                  <div className="shrink-0 text-end">
                    <p className="num text-sm font-bold text-ink">{Math.round(share * 100)} %</p>
                    <p className="num text-xs text-inkSoft">{formatCatalogPrice(entry.revenue)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
